class HumbleScentsGrid extends HTMLElement {
  #swiper = null;
  #thumbsSwiper = null;
  #retries = 0;
  #allSlides = [];
  #allThumbs = [];
  #activeFamily = '';
  static #MAX_RETRIES = 50;

  connectedCallback() {
    this.addEventListener('click', this.#onSegmentClick);
    document.addEventListener('humble-scents:filter', this.#onFilter);
    this.#captureSlides();
    this.#initSwiper();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onSegmentClick);
    document.removeEventListener('humble-scents:filter', this.#onFilter);
    this.#swiper?.destroy(true, true);
    this.#thumbsSwiper?.destroy(true, true);
    this.#swiper = null;
    this.#thumbsSwiper = null;
  }

  // Snapshot every slide + thumb at boot so we can filter by family later.
  // Filtering rebuilds the wrapper from this list instead of mutating the DOM
  // destructively (avoids data loss when toggling between families).
  #captureSlides() {
    this.#allSlides = Array.from(this.querySelectorAll('.humble-scents-grid__slide'));
    this.#allThumbs = Array.from(this.querySelectorAll('.humble-scents-grid__thumb'));
  }

  #onFilter = (event) => {
    const family = ((event.detail && event.detail.family) || '').trim().toLowerCase();
    if (family === this.#activeFamily) return;
    this.#activeFamily = family;

    const mainWrap = this.querySelector('.humble-scents-grid__swiper .swiper-wrapper');
    const thumbsWrap = this.querySelector('.humble-scents-grid__thumbs .swiper-wrapper');
    if (!mainWrap || !thumbsWrap) return;

    const matchSlide = (el) => !family || (el.dataset.family || '').toLowerCase() === family;

    const visibleSlides = this.#allSlides.filter(matchSlide);
    const visibleThumbs = this.#allThumbs.filter(matchSlide);

    // Tear down the current Swiper instances before re-attaching slides.
    this.#swiper?.destroy(true, true);
    this.#thumbsSwiper?.destroy(true, true);
    this.#swiper = null;
    this.#thumbsSwiper = null;

    mainWrap.replaceChildren(...visibleSlides);
    thumbsWrap.replaceChildren(...visibleThumbs);

    this.#initSwiper();
  };

  #onSegmentClick = (event) => {
    if (!this.#swiper) return;
    const segment = event.target.closest('.humble-scents-grid__intensity-segment');
    if (!segment) return;
    const index = parseInt(segment.dataset.index, 10);
    if (!Number.isNaN(index)) this.#swiper.slideTo(index);
  };

  #initSwiper() {
    if (!window.Swiper) {
      if (this.#retries >= HumbleScentsGrid.#MAX_RETRIES) return;
      this.#retries++;
      setTimeout(() => this.#initSwiper(), 100);
      return;
    }

    const mainEl = this.querySelector('.humble-scents-grid__swiper');
    const thumbsEl = this.querySelector('.humble-scents-grid__thumbs');
    if (!mainEl) return;

    const slides = this.querySelectorAll('.humble-scents-grid__slide');
    if (slides.length <= 1) return;

    if (thumbsEl) {
      this.#thumbsSwiper = new window.Swiper(thumbsEl, {
        slidesPerView: 'auto',
        spaceBetween: 12,
        watchSlidesProgress: true,
        slideToClickedSlide: true,
        breakpoints: {
          990: { spaceBetween: 32 },
        },
      });
      this.#centerThumbsIfFit(thumbsEl);
    }

    const autoplayEnabled = this.dataset.autoplay === 'true';
    const autoplayDelay = parseInt(this.dataset.autoplayDelay, 10) || 5000;

    this.#swiper = new window.Swiper(mainEl, {
      loop: true,
      slidesPerView: 1,
      speed: 400,
      allowTouchMove: true,
      autoplay: autoplayEnabled
        ? { delay: autoplayDelay, disableOnInteraction: false, pauseOnMouseEnter: true }
        : false,
      thumbs: this.#thumbsSwiper ? { swiper: this.#thumbsSwiper } : undefined,
      a11y: {
        prevSlideMessage: 'Previous scent',
        nextSlideMessage: 'Next scent',
      },
      on: {
        slideChange: () => this.#syncNav(),
      },
    });
  }

  #centerThumbsIfFit(thumbsEl) {
    const wrap = thumbsEl.querySelector('.swiper-wrapper');
    if (!wrap) return;
    const thumbs = [...thumbsEl.querySelectorAll('.humble-scents-grid__thumb')];
    if (!thumbs.length) return;
    const gap = window.innerWidth >= 990 ? 32 : 12;
    const totalWidth = thumbs.reduce((acc, t) => acc + t.offsetWidth, 0) + gap * (thumbs.length - 1);
    wrap.style.justifyContent = totalWidth < thumbsEl.offsetWidth ? 'center' : '';
  }

  #syncNav() {
    if (!this.#swiper) return;
    const index = this.#swiper.realIndex;
    for (const bar of this.querySelectorAll('.humble-scents-grid__intensity-bar')) {
      const segments = bar.querySelectorAll('.humble-scents-grid__intensity-segment');
      for (const [i, seg] of segments.entries()) {
        seg.classList.toggle('humble-scents-grid__intensity-segment--active', i === index);
      }
    }
  }
}

customElements.define('humble-scents-grid', HumbleScentsGrid);
